import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Clock, Droplet } from "lucide-react";
import { Button } from "./ui/button";
import {
  measureCardValue,
  measureEyebrow,
  measureSectionTitle,
} from "../utils/measurePageTypography";

const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

function formatReadingStamp(d: Date) {
  const wk = WEEK[d.getDay()];
  const mon = MONTHS[d.getMonth()];
  const day = String(d.getDate()).padStart(2, "0");
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${wk} · ${mon} ${day} · ${h}:${m} ${ampm}`;
}

function isoDateLocal(d: Date) {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function hhmmLocal(d: Date) {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function parseLocalDateTime(dateStr: string, timeStr: string): Date {
  const [y, mo, day] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  return new Date(y, (mo ?? 1) - 1, day ?? 1, hh ?? 0, mm ?? 0, 0, 0);
}

function defaultYesterdayIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return isoDateLocal(d);
}

function formatTimeLabel(timeStr: string) {
  const [hhRaw, mmRaw] = timeStr.split(":");
  let h = Number.parseInt(hhRaw ?? "0", 10);
  const m = mmRaw ?? "00";
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function readingFromCurrent(
  current: { value: string; date: string; time: string } | null | undefined,
  date: string,
): PastReadingItem[] {
  if (!current?.value?.trim() || current.date !== date) return [];
  const meta = current.time ? formatTimeLabel(current.time) : "Finger prick";
  return [{ value: `${current.value} mg/dL`, meta }];
}

/** Glucose reading source the user is logging. Backend uses this to pick the
 *  right field (cgm / finger_prick / fasting / hba1c) and predict the spike. */
export type ReadingType = "finger_prick" | "cgm" | "fasting" | "hba1c";

export type MealTiming = "pre-meal" | "post-meal";

export const MEAL_TIMING_OPTIONS: ReadonlyArray<{
  key: MealTiming;
  label: string;
}> = [
  { key: "pre-meal", label: "Pre-meal" },
  { key: "post-meal", label: "Post-meal" },
];

export const READING_TYPES: ReadonlyArray<{
  key: ReadingType;
  label: string;
  unit: string;
}> = [
  { key: "finger_prick", label: "Finger prick", unit: "mg/dL" },
  { key: "cgm", label: "CGM", unit: "mg/dL" },
  { key: "fasting", label: "Fasting glucose", unit: "mg/dL" },
  { key: "hba1c", label: "HbA1c", unit: "%" },
];

const GLUCOSE_MG_DL_MIN = 40;
const GLUCOSE_MG_DL_MAX = 600;
const HBA1C_MIN = 3;
const HBA1C_MAX = 20;

function validateGlucoseReading(
  type: ReadingType,
  raw: string,
): { isValid: boolean; error: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { isValid: false, error: null };
  }

  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n)) {
    return { isValid: false, error: "Enter a valid number" };
  }

  if (type === "hba1c") {
    if (n < HBA1C_MIN || n > HBA1C_MAX) {
      return {
        isValid: false,
        error: `HbA1c must be between ${HBA1C_MIN} and ${HBA1C_MAX}%`,
      };
    }
    return { isValid: true, error: null };
  }

  if (n < GLUCOSE_MG_DL_MIN || n > GLUCOSE_MG_DL_MAX) {
    return {
      isValid: false,
      error: `Reading must be between ${GLUCOSE_MG_DL_MIN} and ${GLUCOSE_MG_DL_MAX} mg/dL`,
    };
  }

  return { isValid: true, error: null };
}

export type FingerPrickSavePayload = {
  /** Numeric value entered (mg/dL for glucose types, % for HbA1c). */
  mgDl: number;
  /** Which reading source this value belongs to. */
  readingType: ReadingType;
  /** Whether the reading was taken before or after a meal. */
  mealTiming: MealTiming;
  mode: "now" | "past";
  /** ISO — when user picks a custom date/time (past mode or now + manual time). */
  loggedAt?: string;
  /** On LOG NOW: user chose date/time instead of server auto-stamp. */
  useManualTime?: boolean;
};

export type PastReadingItem = { value: string; meta: string };

type FingerPrickLogSheetProps = {
  open: boolean;
  onClose: () => void;
  initialMode?: "now" | "past";
  onSaved?: (payload: FingerPrickSavePayload) => void | Promise<void>;
  /** Last saved finger-prick reading — shown as history in Past tab */
  currentReading?: { value: string; date: string; time: string } | null;
  /** Fetch finger prick readings for a given date (YYYY-MM-DD) */
  onFetchReadingsForDate?: (date: string) => Promise<PastReadingItem[]>;
};

/**
 * Bottom sheet — log finger-stick glucose (NOW = auto time, PAST = date/time).
 */
const FingerPrickLogSheet = ({
  open,
  onClose,
  initialMode = "now",
  onSaved,
  currentReading,
  onFetchReadingsForDate,
}: FingerPrickLogSheetProps) => {
  const [mode, setMode] = useState<"now" | "past">(initialMode);
  const [readingType, setReadingType] = useState<ReadingType>("finger_prick");
  const [mealTiming, setMealTiming] = useState<MealTiming>("pre-meal");
  const [typeOpen, setTypeOpen] = useState(false);
  const [mealOpen, setMealOpen] = useState(false);
  const typeRef = useRef<HTMLDivElement>(null);
  const mealRef = useRef<HTMLDivElement>(null);
  const [reading, setReading] = useState("");
  const [pastDate, setPastDate] = useState(() => defaultYesterdayIso());
  const [pastTime, setPastTime] = useState("09:42");
  const [useManualTime, setUseManualTime] = useState(false);
  const [dateReadings, setDateReadings] = useState<PastReadingItem[]>([]);
  const [dateReadingsLoading, setDateReadingsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /** Prevents double PUT when Save is tapped/clicked twice quickly. */
  const saveLockRef = useRef(false);

  // Keep a stable ref so the fetch effect doesn't re-fire on every parent render
  const fetchRef = useRef(onFetchReadingsForDate);
  useEffect(() => { fetchRef.current = onFetchReadingsForDate; });

  const todayIso = isoDateLocal(new Date());

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setReadingType("finger_prick");
    setMealTiming("pre-meal");
    setTypeOpen(false);
    setMealOpen(false);
    setPastDate(isoDateLocal(new Date()));
    setPastTime(hhmmLocal(new Date()));
    setUseManualTime(false);
    setReading("");
    setDateReadings([]);
    setIsSaving(false);
    saveLockRef.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMode]);

  useEffect(() => {
    if (!open || mode !== "past") return;
    const fn = fetchRef.current;

    if (!fn) {
      setDateReadingsLoading(false);
      setDateReadings(readingFromCurrent(currentReading, pastDate));
      return;
    }

    let cancelled = false;
    setDateReadingsLoading(true);
    setDateReadings([]);
    fn(pastDate)
      .then((items) => {
        if (!cancelled) {
          setDateReadings(items);
          setDateReadingsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDateReadings(readingFromCurrent(currentReading, pastDate));
          setDateReadingsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, pastDate, currentReading]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (typeOpen) setTypeOpen(false);
        else if (mealOpen) setMealOpen(false);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, typeOpen, mealOpen]);

  useEffect(() => {
    if (!typeOpen && !mealOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (typeOpen && typeRef.current && !typeRef.current.contains(e.target as Node)) {
        setTypeOpen(false);
      }
      if (mealOpen && mealRef.current && !mealRef.current.contains(e.target as Node)) {
        setMealOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [typeOpen, mealOpen]);

  const nowTimeMax = () => hhmmLocal(new Date());

  const timeMaxForDate = pastDate === todayIso ? nowTimeMax() : "23:59";

  const clampTimeIfNeeded = (dateStr: string, timeStr: string) => {
    if (dateStr !== todayIso) return timeStr;
    const cap = nowTimeMax();
    return timeStr > cap ? cap : timeStr;
  };

  if (!open) return null;

  const stamped = formatReadingStamp(new Date());
  const activeType =
    READING_TYPES.find((t) => t.key === readingType) ?? READING_TYPES[0];
  const activeMealTiming =
    MEAL_TIMING_OPTIONS.find((t) => t.key === mealTiming) ?? MEAL_TIMING_OPTIONS[0];
  const isPercent = readingType === "hba1c";
  const readingValidation = validateGlucoseReading(readingType, reading);
  const showReadingError = reading.trim() !== "" && !readingValidation.isValid;
  const canSave = readingValidation.isValid && !isSaving;

  const handleReadingChange = (raw: string) => {
    if (isPercent) {
      // Allow a single decimal point, e.g. "5.7" (max 2 integer + 1 fraction digit)
      let v = raw.replace(/[^\d.]/g, "");
      const [intPart = "", ...rest] = v.split(".");
      v = rest.length
        ? `${intPart.slice(0, 2)}.${rest.join("").replace(/\./g, "").slice(0, 1)}`
        : intPart.slice(0, 2);
      setReading(v);
    } else {
      setReading(raw.replace(/\D/g, "").slice(0, 3));
    }
  };

  const handleSave = async () => {
    if (!readingValidation.isValid) return;
    const n = Number.parseFloat(reading);
    if (Number.isNaN(n)) return;
    // One save → one PUT. Block rapid re-entry (double tap / key repeat).
    if (saveLockRef.current || isSaving) return;
    saveLockRef.current = true;
    setIsSaving(true);

    try {
      let payload: FingerPrickSavePayload;
      if (mode === "past" || (mode === "now" && useManualTime)) {
        const safeTime = clampTimeIfNeeded(pastDate, pastTime);
        const logged = parseLocalDateTime(pastDate, safeTime);
        if (logged.getTime() > Date.now()) {
          saveLockRef.current = false;
          setIsSaving(false);
          return;
        }
        payload = {
          mgDl: n,
          readingType,
          mealTiming,
          mode,
          loggedAt: logged.toISOString(),
          useManualTime: mode === "now" ? true : undefined,
        };
      } else {
        payload = { mgDl: n, readingType, mealTiming, mode: "now" };
      }

      await onSaved?.(payload);
      onClose();
    } catch {
      saveLockRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex flex-col justify-end" role="presentation">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />

      <div className="relative z-10 mx-auto w-full max-w-[400px] duration-200 animate-in slide-in-from-bottom-4">
        <div className="max-h-[min(92dvh,_820px)] overflow-y-auto scrollbar-hide rounded-t-3xl border border-border bg-background shadow-2xl ring-1 ring-border/80">
          <div className="flex justify-center pb-2 pt-3">
            <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          <div className="px-5 pb-1 pt-0.5">
            <p className={measureEyebrow}>{activeType.label} · Log reading</p>
          </div>

          <div className="space-y-4 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom,1rem))] pt-2">
            <h2 className={`text-center ${measureSectionTitle}`}>
              {mode === "now"
                ? "What\u2019s your current reading?"
                : "When did you take this reading?"}
            </h2>

            {mode === "now" ? (
              <>
                <div className="flex items-center gap-3 rounded-xl border border-emerald-200/90 bg-emerald-50/95 p-3 dark:border-emerald-800/50 dark:bg-emerald-950/40">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 dark:bg-emerald-500">
                    <Clock className="h-5 w-5 text-white" strokeWidth={1.85} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold tracking-tight text-foreground">{stamped}</p>
                    <p className="mt-1 text-2xs font-bold uppercase leading-snug tracking-[0.07em] text-emerald-950 dark:text-emerald-100">
                      {useManualTime
                        ? "Custom date & time · sent to server"
                        : "Auto-stamped · server time on save"}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setUseManualTime((v) => !v)}
                  className="text-left text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  {useManualTime ? "Use current time instead" : "Set a different date & time"}
                </button>

                {useManualTime ? (
                  <>
                    <div className="space-y-1.5">
                      <label
                        htmlFor="now-custom-date"
                        className="block text-2xs font-semibold uppercase tracking-[0.1em] text-foreground/70 dark:text-foreground/75"
                      >
                        Date
                      </label>
                      <input
                        id="now-custom-date"
                        type="date"
                        value={pastDate}
                        max={todayIso}
                        onChange={(e) => {
                          if (e.target.value) setPastDate(e.target.value);
                        }}
                        className="w-full rounded-2xl border border-border bg-muted/40 px-4 py-3 text-base font-medium text-foreground outline-none focus:ring-2 focus:ring-ring dark:bg-muted/30"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label
                        htmlFor="now-custom-time"
                        className="block text-2xs font-semibold uppercase tracking-[0.1em] text-foreground/70 dark:text-foreground/75"
                      >
                        Time
                      </label>
                      <input
                        id="now-custom-time"
                        type="time"
                        value={pastTime}
                        max={timeMaxForDate}
                        onChange={(e) => {
                          if (e.target.value) setPastTime(e.target.value);
                        }}
                        className="w-full rounded-2xl border border-border bg-muted/40 px-4 py-3 text-base font-medium text-foreground outline-none focus:ring-2 focus:ring-ring dark:bg-muted/30"
                      />
                    </div>
                  </>
                ) : null}
              </>
            ) : (
              <>
                {/* Date picker */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="past-date"
                    className="block text-2xs font-semibold uppercase tracking-[0.1em] text-foreground/70 dark:text-foreground/75"
                  >
                    Date
                  </label>
                  <input
                    id="past-date"
                    type="date"
                    value={pastDate}
                    max={todayIso}
                    onChange={(e) => {
                      if (e.target.value) setPastDate(e.target.value);
                    }}
                    className="w-full rounded-2xl border border-border bg-muted/40 px-4 py-3 text-base font-medium text-foreground outline-none focus:ring-2 focus:ring-ring dark:bg-muted/30"
                  />
                </div>

                {/* Time picker */}
                <div className="space-y-1.5">
                  <label
                    htmlFor="past-time"
                    className="block text-2xs font-semibold uppercase tracking-[0.1em] text-foreground/70 dark:text-foreground/75"
                  >
                    Time
                  </label>
                  <input
                    id="past-time"
                    type="time"
                    value={pastTime}
                    max={timeMaxForDate}
                    onChange={(e) => {
                      if (e.target.value) setPastTime(e.target.value);
                    }}
                    className="w-full rounded-2xl border border-border bg-muted/40 px-4 py-3 text-base font-medium text-foreground outline-none focus:ring-2 focus:ring-ring dark:bg-muted/30"
                  />
                </div>

                {/* Past readings for selected date */}
                {dateReadingsLoading && (
                  <div className="h-16 animate-pulse rounded-2xl bg-muted/60" aria-hidden />
                )}
                {!dateReadingsLoading && dateReadings.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-2xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      Past readings
                    </p>
                    <ul className="space-y-2">
                      {dateReadings.map((r, i) => (
                        <li key={i} className="flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-muted">
                            <Droplet className="h-4 w-4 text-muted-foreground" strokeWidth={1.85} aria-hidden />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-2xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                              {r.meta}
                            </p>
                            <p className={measureCardValue}>{r.value}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!dateReadingsLoading && dateReadings.length === 0 && (
                  <p className="text-sm text-muted-foreground">No readings found for this date.</p>
                )}
              </>
            )}

            {(mode === "now" || mode === "past") && (
              <>
                {/* Reading type selector — theme-colored custom dropdown so the
                    highlight matches the app instead of the OS default blue. */}
                <div ref={typeRef} className="relative">
                  <span className="mb-1.5 block text-2xs font-semibold uppercase tracking-[0.1em] text-foreground/70 dark:text-foreground/75">
                    Reading type
                  </span>
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={typeOpen}
                    onClick={() => {
                      setMealOpen(false);
                      setTypeOpen((v) => !v);
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-muted/40 px-3 py-3 text-base font-medium text-foreground outline-none focus:ring-2 focus:ring-ring dark:bg-muted/30"
                  >
                    <span>{activeType.label}</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${typeOpen ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </button>

                  {typeOpen && (
                    <ul
                      role="listbox"
                      className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-xl ring-1 ring-border/60"
                    >
                      {READING_TYPES.map((t) => {
                        const selected = t.key === readingType;
                        return (
                          <li key={t.key} role="option" aria-selected={selected}>
                            <button
                              type="button"
                              onClick={() => {
                                setReadingType(t.key);
                                setReading("");
                                setTypeOpen(false);
                              }}
                              className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                                selected
                                  ? "bg-primary/10 text-primary"
                                  : "text-foreground hover:bg-muted"
                              }`}
                            >
                              <span>{t.label}</span>
                              {selected && <Check className="h-4 w-4 shrink-0" aria-hidden />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div ref={mealRef} className="relative">
                  <span className="mb-1.5 block text-2xs font-semibold uppercase tracking-[0.1em] text-foreground/70 dark:text-foreground/75">
                    Meal timing
                  </span>
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={mealOpen}
                    onClick={() => {
                      setTypeOpen(false);
                      setMealOpen((v) => !v);
                    }}
                    className="flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-muted/40 px-3 py-3 text-base font-medium text-foreground outline-none focus:ring-2 focus:ring-ring dark:bg-muted/30"
                  >
                    <span>{activeMealTiming.label}</span>
                    <ChevronDown
                      className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${mealOpen ? "rotate-180" : ""}`}
                      aria-hidden
                    />
                  </button>

                  {mealOpen && (
                    <ul
                      role="listbox"
                      className="absolute z-20 mt-1.5 w-full overflow-hidden rounded-xl border border-border bg-popover py-1 shadow-xl ring-1 ring-border/60"
                    >
                      {MEAL_TIMING_OPTIONS.map((t) => {
                        const selected = t.key === mealTiming;
                        return (
                          <li key={t.key} role="option" aria-selected={selected}>
                            <button
                              type="button"
                              onClick={() => {
                                setMealTiming(t.key);
                                setMealOpen(false);
                              }}
                              className={`flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                                selected
                                  ? "bg-primary/10 text-primary"
                                  : "text-foreground hover:bg-muted"
                              }`}
                            >
                              <span>{t.label}</span>
                              {selected && <Check className="h-4 w-4 shrink-0" aria-hidden />}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="finger-reading-mgdl"
                    className={`mb-1.5 block text-2xs font-semibold uppercase tracking-[0.1em] ${
                      showReadingError
                        ? "text-destructive"
                        : "text-foreground/70 dark:text-foreground/75"
                    }`}
                  >
                    Reading ({activeType.unit})
                  </label>
                  <div
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${
                      showReadingError
                        ? "border-destructive bg-destructive/5 dark:bg-destructive/10"
                        : "border-border bg-muted/40 dark:bg-muted/30"
                    }`}
                  >
                    <input
                      id="finger-reading-mgdl"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      aria-label={`${activeType.label} reading in ${activeType.unit}`}
                      aria-invalid={showReadingError}
                      value={reading}
                      onChange={(e) => handleReadingChange(e.target.value)}
                      placeholder={isPercent ? "5.7" : "110"}
                      className={`min-w-0 flex-1 border-0 bg-transparent p-0 text-2xl font-semibold leading-none tracking-tight outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                        showReadingError
                          ? "text-destructive placeholder:text-destructive/40"
                          : "text-foreground placeholder:text-muted-foreground/40"
                      }`}
                    />
                    <span
                      className={`shrink-0 text-2xs font-bold uppercase tracking-[0.06em] ${
                        showReadingError
                          ? "text-destructive"
                          : "text-foreground/65 dark:text-foreground/70"
                      }`}
                    >
                      {activeType.unit}
                    </span>
                  </div>
                  {showReadingError && readingValidation.error ? (
                    <p className="mt-1.5 text-sm font-medium text-destructive">
                      {readingValidation.error}
                    </p>
                  ) : null}
                </div>

                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    disabled={!canSave}
                    className="h-12 flex-[7] rounded-xl bg-foreground text-base font-semibold text-background hover:bg-foreground/90 disabled:opacity-50"
                    onClick={() => void handleSave()}
                  >
                    {isSaving ? "Saving…" : "Save reading"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSaving}
                    className="h-12 flex-[3] rounded-xl border-border bg-card font-medium"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FingerPrickLogSheet;
